import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { 
  FaPlug, FaKey, FaMobileAlt, FaCheckCircle, FaTimesCircle, 
  FaHashtag, FaPlus, FaTrash, FaInfoCircle, FaShieldAlt, FaServer
} from 'react-icons/fa';

// --- PREMIUM SAAS THEME ---
const THEME = {
  primary: '#0ea5e9',    // Ocean Blue
  primaryDark: '#0284c7',
  success: '#10b981',    // Emerald Green
  warning: '#f59e0b',    // Amber
  error: '#ef4444',      // Red
  bg: '#f8fafc',         // Slate 50
  surface: '#ffffff',    // White
  textMain: '#0f172a',   // Slate 900
  textSub: '#64748b',    // Slate 500
  border: '#e2e8f0',     // Slate 200
  waGreen: '#25D366'
};

function ConnectManager() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('API'); // 'API' or 'TAGS'
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // API Credentials State
  const [credentials, setCredentials] = useState({
    accessToken: "",
    phoneId: "",
    phone: "",
    wabaId: "", // WhatsApp Business Account ID (Good to have)
    status: "disconnected"
  });

  // Tag Management State
  const [tags, setTags] = useState([]);
  const [newTagStr, setNewTagStr] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        // 🔥 FIX: Fetch API Settings (Check Root doc first for Embedded Setup compatibility)
        const rootRef = doc(db, "businesses", u.uid);
        const rootSnap = await getDoc(rootRef);
        
        let foundCreds = false;
        
        if (rootSnap.exists() && rootSnap.data().accessToken) {
           setCredentials(prev => ({
             ...prev,
             accessToken: rootSnap.data().accessToken,
             phoneId: rootSnap.data().phoneId,
             phone: rootSnap.data().phoneNumber || "",
             wabaId: rootSnap.data().accountId || "",
             status: rootSnap.data().status || "connected"
           }));
           foundCreds = true;
        }

        // Fallback to manual settings doc if Embedded Setup hasn't been used
        if (!foundCreds) {
            const apiRef = doc(db, "businesses", u.uid, "settings", "whatsapp");
            const apiSnap = await getDoc(apiRef);
            if (apiSnap.exists() && apiSnap.data().accessToken) {
              setCredentials(prev => ({ ...prev, ...apiSnap.data() }));
            }
        }

        // Fetch CRM Tags
        const tagsRef = doc(db, "businesses", u.uid, "settings", "crm_tags");
        const tagsSnap = await getDoc(tagsRef);
        if (tagsSnap.exists() && tagsSnap.data().list) {
          setTags(tagsSnap.data().list);
        }
      }
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      unsubscribe();
    };
  }, []);

  // --- API CONNECTION HANDLER ---
  const handleSaveAPI = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (credentials.accessToken.length < 50) {
      return setSaveStatus({ type: 'error', message: 'Invalid Token. Please paste the full Permanent Access Token.' });
    }
    
    setLoading(true);
    setSaveStatus({ type: '', message: '' });

    try {
      // Save manual entries to BOTH locations to keep everything 100% in sync
      const configRef = doc(db, "businesses", user.uid, "settings", "whatsapp");
      const rootRef = doc(db, "businesses", user.uid);

      const payload = {
        accessToken: credentials.accessToken.trim(),
        phoneNumberId: credentials.phoneId.trim(),
        phoneId: credentials.phoneId.trim(), 
        phone: credentials.phone.trim(),
        wabaId: credentials.wabaId.trim(),
        status: "connected",
        lastUpdated: serverTimestamp()
      };

      await setDoc(configRef, payload, { merge: true });
      await setDoc(rootRef, payload, { merge: true });

      setCredentials(prev => ({ ...prev, status: "connected" }));
      setSaveStatus({ type: 'success', message: 'API Credentials securely saved and linked!' });
    } catch (error) {
      setSaveStatus({ type: 'error', message: "Error: " + error.message });
    } finally {
      setLoading(false);
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
    }
  };

  // --- TAG MANAGEMENT HANDLERS ---
  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTagStr.trim()) return;
    
    // Format tag: remove spaces, special chars, make uppercase (e.g. "Deep Tissue" -> "DEEP_TISSUE")
    const formattedTag = newTagStr.trim().toUpperCase().replace(/[^A-Z0-9]/g, '_');
    
    if (tags.includes(formattedTag)) {
      setNewTagStr("");
      return setSaveStatus({ type: 'warning', message: 'Tag already exists!' });
    }

    const updatedTags = [...tags, formattedTag];
    setTags(updatedTags);
    setNewTagStr("");

    try {
      await setDoc(doc(db, "businesses", user.uid, "settings", "crm_tags"), { list: updatedTags }, { merge: true });
      setSaveStatus({ type: 'success', message: 'Tag added successfully.' });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    const updatedTags = tags.filter(t => t !== tagToRemove);
    setTags(updatedTags);
    try {
      await setDoc(doc(db, "businesses", user.uid, "settings", "crm_tags"), { list: updatedTags }, { merge: true });
    } catch (error) {
      console.error(error);
    }
  };

  if (!user) return <div style={{ padding: '40px', textAlign: 'center', fontWeight: 'bold', color: THEME.textSub }}>Loading Environment...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: THEME.bg, minHeight: '100vh', padding: isMobile ? '15px' : '40px' }}>
      
      {/* PAGE HEADER */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '900', color: THEME.textMain, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaServer color={THEME.primary} /> Settings & Integrations
        </h1>
        <p style={{ color: THEME.textSub, margin: 0, fontSize: '15px' }}>Manage your WhatsApp Cloud API connection and CRM preferences.</p>
      </div>

      <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '30px' }}>
        
        {/* SIDEBAR NAVIGATION */}
        <div style={{ width: isMobile ? '100%' : '250px', flexShrink: 0 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '10px', border: `1px solid ${THEME.border}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
            <button onClick={() => setActiveTab('API')} style={{ width: '100%', padding: '14px 16px', borderRadius: '10px', border: 'none', background: activeTab === 'API' ? '#f0f9ff' : 'transparent', color: activeTab === 'API' ? THEME.primaryDark : THEME.textSub, fontWeight: '700', fontSize: '14px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s', marginBottom: '5px' }}>
              <FaPlug size={16} /> Meta API Setup
            </button>
            <button onClick={() => setActiveTab('TAGS')} style={{ width: '100%', padding: '14px 16px', borderRadius: '10px', border: 'none', background: activeTab === 'TAGS' ? '#f0f9ff' : 'transparent', color: activeTab === 'TAGS' ? THEME.primaryDark : THEME.textSub, fontWeight: '700', fontSize: '14px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s' }}>
              <FaHashtag size={16} /> Smart Tags
            </button>
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div style={{ flex: 1 }}>
          
          {/* GLOBAL TOAST NOTIFICATION */}
          {saveStatus.message && (
            <div style={{ padding: '15px 20px', borderRadius: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '700', animation: 'fadeIn 0.3s',
              background: saveStatus.type === 'error' ? '#fef2f2' : saveStatus.type === 'warning' ? '#fffbeb' : '#ecfdf5',
              color: saveStatus.type === 'error' ? '#b91c1c' : saveStatus.type === 'warning' ? '#b45309' : '#047857',
              border: `1px solid ${saveStatus.type === 'error' ? '#fecaca' : saveStatus.type === 'warning' ? '#fde68a' : '#a7f3d0'}`
            }}>
              {saveStatus.type === 'error' ? <FaTimesCircle size={18}/> : <FaCheckCircle size={18}/>}
              {saveStatus.message}
            </div>
          )}

          {/* ========================================================
              TAB 1: META API SETUP
          ======================================================== */}
          {activeTab === 'API' && (
            <div style={{ animation: 'fadeIn 0.4s' }}>
              <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', borderRadius: '20px', padding: '30px', color: 'white', marginBottom: '25px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}><FaShieldAlt color={THEME.success}/> Secure Connection</h2>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: '1.5' }}>Link your Meta Developer credentials to enable automated messaging, broadcasting, and AI responses.</p>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 20px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: credentials.status === 'connected' ? THEME.success : THEME.error, boxShadow: `0 0 10px ${credentials.status === 'connected' ? THEME.success : THEME.error}` }}></div>
                    <span style={{ fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>{credentials.status}</span>
                  </div>
                </div>
                <FaPlug size={180} style={{ position: 'absolute', right: '-20px', bottom: '-40px', opacity: 0.05, zIndex: 1, transform: 'rotate(-20deg)' }}/>
              </div>

              <form onSubmit={handleSaveAPI} style={{ background: 'white', borderRadius: '20px', padding: isMobile ? '20px' : '35px', border: `1px solid ${THEME.border}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '800', color: THEME.textMain, textTransform: 'uppercase', marginBottom: '10px' }}>
                    <FaKey color={THEME.primary} /> System Access Token
                  </label>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: THEME.textSub }}>Must be a Permanent Token generated from your Meta Business Settings.</p>
                  <textarea 
                    required
                    value={credentials.accessToken} 
                    onChange={e => setCredentials({...credentials, accessToken: e.target.value})} 
                    placeholder="EAAPOnj5ytocBQZCPP..."
                    style={{ width: '100%', padding: '16px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontFamily: 'monospace', minHeight: '120px', resize: 'vertical', boxSizing: 'border-box', background: '#f8fafc', color: THEME.textMain }} 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '35px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: THEME.textMain, textTransform: 'uppercase', marginBottom: '10px' }}>Phone Number ID</label>
                    <input required type="text" value={credentials.phoneId} onChange={e => setCredentials({...credentials, phoneId: e.target.value})} placeholder="e.g. 1050657121456282" style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: '#f8fafc', color: THEME.textMain, fontWeight: '600' }} />
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '800', color: THEME.textMain, textTransform: 'uppercase', marginBottom: '10px' }}><FaMobileAlt/> Display Number</label>
                    <input required type="text" value={credentials.phone} onChange={e => setCredentials({...credentials, phone: e.target.value})} placeholder="e.g. 919974295999" style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: '#f8fafc', color: THEME.textMain, fontWeight: '600' }} />
                  </div>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: THEME.textMain, textTransform: 'uppercase', marginBottom: '10px' }}>WABA ID (Optional)</label>
                    <input type="text" value={credentials.wabaId} onChange={e => setCredentials({...credentials, wabaId: e.target.value})} placeholder="WhatsApp Business Account ID" style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: '#f8fafc', color: THEME.textMain, fontWeight: '600' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${THEME.border}`, paddingTop: '25px' }}>
                  <button type="submit" disabled={loading} style={{ background: THEME.primary, color: 'white', border: 'none', padding: '16px 32px', borderRadius: '12px', fontWeight: '800', fontSize: '15px', cursor: loading ? 'wait' : 'pointer', boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)', transition: 'transform 0.2s' }}>
                    {loading ? 'Authenticating...' : 'Save & Link Engine'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ========================================================
              TAB 2: SMART TAGS (CRM SEGMENTATION)
          ======================================================== */}
          {activeTab === 'TAGS' && (
            <div style={{ animation: 'fadeIn 0.4s' }}>
              <div style={{ background: 'white', borderRadius: '20px', padding: isMobile ? '20px' : '35px', border: `1px solid ${THEME.border}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                
                <div style={{ marginBottom: '30px' }}>
                  <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: THEME.textMain }}>Smart Audience Tags</h2>
                  <p style={{ margin: 0, color: THEME.textSub, fontSize: '14px', lineHeight: '1.5' }}>Create tags to organize your contacts. You can automatically assign tags using the Bot Manager, and filter by these tags when sending Broadcasts or Drip Campaigns.</p>
                </div>

                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: `1px solid ${THEME.border}`, marginBottom: '30px' }}>
                  <form onSubmit={handleAddTag} style={{ display: 'flex', gap: '15px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <FaHashtag color={THEME.textSub} style={{ position: 'absolute', top: '16px', left: '16px' }} />
                      <input 
                        type="text" 
                        value={newTagStr} 
                        onChange={e => setNewTagStr(e.target.value)} 
                        placeholder="e.g. VIP_CLIENT, DEEP_TISSUE, LOST_LEAD" 
                        style={{ width: '100%', padding: '14px 16px 14px 45px', borderRadius: '12px', border: `1px solid ${THEME.border}`, outline: 'none', fontSize: '14px', boxSizing: 'border-box', fontWeight: '600', color: THEME.textMain }} 
                      />
                    </div>
                    <button type="submit" disabled={!newTagStr.trim()} style={{ background: THEME.textMain, color: 'white', border: 'none', padding: '14px 24px', borderRadius: '12px', fontWeight: '800', fontSize: '14px', cursor: newTagStr.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <FaPlus /> Create Tag
                    </button>
                  </form>
                </div>

                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '15px' }}>Active Tags ({tags.length})</h3>
                  
                  {tags.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '16px', border: '2px dashed #cbd5e1' }}>
                      <FaInfoCircle size={30} color="#94a3b8" style={{ marginBottom: '15px' }}/>
                      <div style={{ fontWeight: '700', color: THEME.textMain }}>No tags created yet.</div>
                      <div style={{ fontSize: '13px', color: THEME.textSub, marginTop: '5px' }}>Create your first tag above to start segmenting your CRM.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                      {tags.map(tag => (
                        <div key={tag} style={{ background: 'white', border: `1px solid ${THEME.border}`, padding: '8px 16px', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                          <span style={{ fontSize: '13px', fontWeight: '800', color: THEME.primaryDark }}>#{tag}</span>
                          <button onClick={() => handleRemoveTag(tag)} style={{ background: 'none', border: 'none', padding: 0, color: THEME.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <FaTimesCircle size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}

export default ConnectManager;